import React from 'react'
import moment from 'moment'
import { DataGrid } from '@mui/x-data-grid'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import HelpCenterIcon from '@mui/icons-material/HelpCenter'
import EPCRCard from '../../../../components/Cards/EPCRCard'
import DetailsBox from '../../../../components/Cards/DetailsBox'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import _news2calc from '../../_news2calc'
import _pewsCalc, {
  hasPewsRequiredFields,
  isUnder18,
  pewsTempWarning,
  resolvePewsAgeBand,
} from '../../_pewsCalc'

const VitalsModule = ({ vitals, spo2Scale2, demographics }) => {
  const paediatric = isUnder18(demographics, vitals?.[0]?.time)
  return (
  <DetailsBox title="Vital Signs">
    <Box sx={{ height: 400, width: '100%' }}>
      <DataGrid
        columns={[
          {
            field: 'time',
            headerName: 'Time',
            // renderCell: (params) => moment(params.row.time).format('HH:mm'),
            valueGetter: (value, row) => moment(row.time).format('HH:mm'),
          },
          {
            field: 'NEWS2',
            headerName: paediatric ? 'PEWS' : 'NEWS2',
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
              const obs = params.row
              const warning = (title) => (
                <Tooltip title={title}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    width="100%"
                  >
                    <HelpCenterIcon color="warning" />
                  </Box>
                </Tooltip>
              )
              const scoreBox = (result) => (
                <Box
                  bgcolor={result.color}
                  width="100%"
                  height="100%"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  color={result.textColor}
                >
                  {result.score}
                </Box>
              )
              if (paediatric) {
                const ageBand = resolvePewsAgeBand(demographics, obs)
                if (!ageBand || !hasPewsRequiredFields(obs)) {
                  return warning(
                    'A PEWS score could not be calculated for this set of observations',
                  )
                }
                return scoreBox(_pewsCalc({ obs, ageBand }))
              }
              if (
                !obs?.respRate ||
                !obs?.spo2 ||
                !obs?.bpSys ||
                !obs?.heartRate ||
                !obs?.avpu ||
                !obs?.temp
              ) {
                return warning(
                  'You must complete all relevant vital signs for a NEWS2 score to be calculated',
                )
              }
              return scoreBox(_news2calc({ obs, spo2Scale2 }))
            },
          },

          {
            field: 'gcs',
            headerName: 'GCS',
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
              const e = params.row.gcsEyes
              const v = params.row.gcsVerbal
              const m = params.row.gcsMotor

              if (!e && !v && !m) {
                return null
              } else if (!e || !v || !m) {
                return <ReportProblemIcon color="warning" />
              } else {
                return (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    width="100%"
                  >
                    <Stack>
                      <Typography>
                        {Number(e) + Number(v) + Number(m)}
                      </Typography>
                      <Typography variant="caption">
                        (E{e},V{v},M{m})
                      </Typography>
                    </Stack>
                  </Box>
                )
              }
            },
          },
          {
            field: 'respRate',
            headerName: 'Resps',
            align: 'center',
            headerAlign: 'center',
          },
          {
            field: 'heartRate',
            headerName: 'Pulse',
            align: 'center',
            headerAlign: 'center',
          },
          {
            field: 'spo2',
            headerName: 'SPO2',
            align: 'center',
            headerAlign: 'center',
          },
          {
            field: 'bp',
            headerName: 'BP',
            align: 'center',
            headerAlign: 'center',
            valueGetter: (value, row) =>
              row.bpSys && row.bpDia
                ? row.bpSys + '/' + row.bpDia
                : row.bpDerogation || undefined,
          },
          {
            field: 'temp',
            headerName: 'Temp',
            valueGetter: (value, row) => row?.temp && `${row.temp}°C`,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
              const tempWarning =
                paediatric && pewsTempWarning(params.row?.temp)
              return (
                <Box
                  width="100%"
                  height="100%"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  bgcolor={tempWarning || undefined}
                  color={tempWarning ? 'white' : 'inherit'}
                >
                  {params.value}
                </Box>
              )
            },
          },

          {
            field: 'bgl',
            headerName: 'BM',
            valueGetter: (value, row) => row?.bgl && `${row.bgl}mmol/L`,
            align: 'center',
            headerAlign: 'center',
          },

          {
            field: 'capRefill',
            headerName: 'CRT',
            valueGetter: (value, row) =>
              row?.capRefill && `${row.capRefill}s`,
            align: 'center',
            headerAlign: 'center',
          },
          { field: 'painScore', headerName: 'Pain' },
          {
            field: 'pefr',
            headerName: 'PEFR',
            align: 'center',
            headerAlign: 'center',
          },
          {
            field: 'etco2',
            headerName: 'ETCO2',
            align: 'center',
            headerAlign: 'center',
          },

          {
            field: 'avpu',
            headerName: 'AVPU',
            align: 'center',
            headerAlign: 'center',
          },
          {
            field: 'suppO2',
            headerName: 'Oxygen',
            align: 'center',
            headerAlign: 'center',
            valueGetter: (value, row) =>
              row?.suppO2 === 'Yes' &&
              (row?.suppO2Fio2 ||
                (row?.respSupport && row?.respSupport !== 'None'))
                ? `Yes (${[
                    row?.suppO2Fio2 && `${row.suppO2Fio2}%`,
                    row?.respSupport !== 'None' && row?.respSupport,
                  ]
                    .filter(Boolean)
                    .join(', ')})`
                : row?.suppO2,
          },
          ...(paediatric
            ? [
                {
                  field: 'respDistress',
                  headerName: 'Distress',
                  align: 'center',
                  headerAlign: 'center',
                },
              ]
            : []),
        ]}
        rows={vitals}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={(row) => moment(row.time).toISOString()}
      />
    </Box>
  </DetailsBox>
  )
}

export default VitalsModule
